import React, { useEffect, useState } from 'react';
import { Button, Chip } from '@progress/kendo-react-buttons';
import { Switch } from '@progress/kendo-react-inputs';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { Grid, GridColumn as Column, GridCustomCellProps, GridRowReorderEvent, GridToolbar } from '@progress/kendo-react-grid';
import { pencilIcon, trashIcon, clockIcon, plusIcon } from '@progress/kendo-svg-icons';
import { fetchApi } from '../../services/api';
import { ViewModel as QuoteRulesResponse } from 'TypeGen/Quote/Rules/view-model';
import { QuoteRuleViewModel } from 'TypeGen/Quote/Rules/quote-rule-view-model';
import { QuoteRuleConditionViewModel } from 'TypeGen/Quote/Rules/quote-rule-condition-view-model';
import { QuoteRuleDimension } from 'TypeGen/Quote/Rules/quote-rule-dimension';
import { FrontInboxViewModel } from 'TypeGen/Quote/Rules/front-inbox-view-model';
import { JsonResponse } from 'TypeGen/json-response';
import { IDNameViewModel } from 'TypeGen/id-name-view-model';
import { Title } from '../../utils/title';
import RuleEditor from './RuleEditor';
import RuleHistory from './RuleHistory';

const DIM_LABEL: Record<number, string> = {
  [QuoteRuleDimension.Source]: 'Source',
  [QuoteRuleDimension.BillingCustomer]: 'Customer',
  [QuoteRuleDimension.VehicleType]: 'Vehicle',
  [QuoteRuleDimension.FrontInbox]: 'Inbox',
};

const chipMargin: React.CSSProperties = { marginRight: 4, marginBottom: 3 };

const conditionChips = (conditions: QuoteRuleConditionViewModel[]): JSX.Element => {
  if (!conditions || conditions.length === 0) {
    return <span className="text-muted">Any quote</span>;
  }
  return <Tooltip anchorElement="target" parentTitle={true} openDelay={250}
    tooltipStyle={{ whiteSpace: 'pre-line', maxWidth: 400, overflowWrap: 'anywhere', textAlign: 'left' }}>
    {conditions.map((c, i) => {
    const labels = c.Values.map(v => v.Label || v.Value);
    const shown = labels.slice(0, 3).join(', ') + (labels.length > 3 ? ` +${labels.length - 3}` : '');
    return <span key={i} title={`${labels.join(', ')}`}
      style={{ ...chipMargin, display: 'inline-block' }}>
      <Chip size="small" rounded="medium" themeColor="base" fillMode="outline"
        style={{ pointerEvents: 'none' }} text={`${DIM_LABEL[c.Dimension]}: ${shown}`} disabled />
    </span>;
  })}</Tooltip>;
};

// Two tiers: outcome actions (line 1, with a semantic dot each) and modifiers/overrides
// (line 2, muted plain text). Dots carry meaning only — teal positive, amber caution, signal negative.
const DOT = { positive: '#0E7C7B', caution: '#D4860C', negative: '#E85D26' };

const actionBadges = (rule: QuoteRuleViewModel): JSX.Element => {
  const a = rule.Actions;

  // Outcome actions — what the rule does to cover/close the quote.
  const outcomes: { color: string; text: string }[] = [];
  if (a.AutoCreate) outcomes.push({ color: DOT.positive, text: 'Create' });
  if (a.BlindBidAsset || a.BlindBidCarrier) {
    const paths: string[] = [];
    if (a.BlindBidAsset) {
      const sig: string[] = [];
      if (a.BlindBidMinPAccept != null) sig.push(`P≥${Math.round(a.BlindBidMinPAccept * 100)}%`);
      if (a.BlindBidMinVehiclesInArea != null) sig.push(`${a.BlindBidMinVehiclesInArea} vehicles`);
      paths.push(`asset${sig.length ? ` ${sig.join(' & ')}` : ''}`);
    }
    if (a.BlindBidCarrier) paths.push(`carrier${a.BlindBidMinCarrierVehiclesInArea != null ? ` ${a.BlindBidMinCarrierVehiclesInArea} vehicles` : ''}`);
    outcomes.push({ color: DOT.caution, text: `Blind bid${paths.length ? ` (${paths.join(' or ')})` : ''}` });
  }
  if (a.AutoOffer) outcomes.push({ color: DOT.positive, text: 'Offer' });
  if (a.PostToCarrierLoadBoard) outcomes.push({ color: DOT.positive, text: 'Load board' });
  if (a.AutoDecline) outcomes.push({ color: DOT.negative, text: 'Decline' });

  // Modifiers / overrides — how, not what. Muted sub-line.
  const mods: string[] = [];
  if (a.UsePriceOptimization) mods.push('Adjust rate');
  if (a.AutoSubmitBid) mods.push('Auto bid');
  if (a.UnitsInAreaMiles != null) mods.push(`Area radius ${a.UnitsInAreaMiles}mi`);
  if (a.AutoOfferMiles != null) mods.push(`Offer radius ${a.AutoOfferMiles}mi`);
  if (a.VehicleReserveMinutes === 0) mods.push('No vehicle reserve');
  else if (a.VehicleReserveMinutes != null) mods.push(`Reserve ${a.VehicleReserveMinutes}m`);
  if (a.MaxPickupHoursOut != null) mods.push(`Pickup ≤${a.MaxPickupHoursOut}h`);

  if (outcomes.length === 0 && mods.length === 0) return <span className="text-muted">—</span>;

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', rowGap: 4, alignItems: 'center' }}>
        {outcomes.map((o, i) => (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginRight: 14, whiteSpace: 'nowrap' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: o.color, flex: '0 0 auto' }} />{o.text}
          </span>
        ))}
      </div>
      {mods.length > 0 && <div className="text-muted small mt-1">{mods.join(' · ')}</div>}
    </div>
  );
};

const QuoteAutomationRules: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState<QuoteRuleViewModel[]>([]);
  const [sources, setSources] = useState<IDNameViewModel[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<IDNameViewModel[]>([]);
  const [frontInboxes, setFrontInboxes] = useState<FrontInboxViewModel[]>([]);
  const [editing, setEditing] = useState<QuoteRuleViewModel | 'new' | null>(null);
  const [historyRule, setHistoryRule] = useState<QuoteRuleViewModel | 'all' | null>(null);

  const load = (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    fetchApi('/api/QuoteRules/List')
      .then((r: QuoteRulesResponse) => {
        setRules(r.Rules);
        setSources(r.Sources);
        setVehicleTypes(r.VehicleTypes);
        setFrontInboxes(r.FrontInboxes);
      })
      .finally(() => { if (showSpinner) setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  // Native Kendo Grid row reordering. The Default (fallback) rule is pinned last and not movable.
  const onRowReorder = (e: GridRowReorderEvent) => {
    const dragged = e.draggedDataItems?.[0] as QuoteRuleViewModel | undefined;
    const target = e.droppedDataItem as QuoteRuleViewModel | undefined;
    if (!dragged || !target || dragged.ID === target.ID || dragged.IsDefault || target.IsDefault) return;

    const ordered = rules.filter(r => !r.IsDefault && r.ID !== dragged.ID);
    let to = ordered.findIndex(r => r.ID === target.ID);
    if (e.dropPosition === 'after') to += 1;
    if (to < 0) to = ordered.length;
    ordered.splice(to, 0, dragged);

    const def = rules.find(r => r.IsDefault);
    setRules(def ? [...ordered, def] : ordered);
    fetchApi('/api/QuoteRules/Reorder', { OrderedRuleIDs: ordered.map(r => r.ID) }, 'post')
      .then((r: JsonResponse) => { if (!r.Success) alert(r.ErrorMessage); load(!r.Success); })   // quiet resync on success, spinner-revert on failure
      .catch((err) => { if (!err?.status) { alert('Unable to reorder rules'); load(); } });
  };

  const toggleEnabled = (rule: QuoteRuleViewModel, enabled: boolean) => {
    setRules(prev => prev.map(x => x.ID === rule.ID ? { ...x, Enabled: enabled } : x));   // optimistic
    fetchApi('/api/QuoteRules/SetEnabled', { ID: rule.ID, Enabled: enabled }, 'post')
      .then((r: JsonResponse) => { if (!r.Success) { alert(r.ErrorMessage); load(); } else load(false); })   // resync RowVersion
      .catch((err) => { if (!err?.status) { alert('Unable to update rule'); load(); } });
  };

  const remove = (rule: QuoteRuleViewModel) => {
    if (!window.confirm(`Delete rule "${rule.Name}"? This cannot be undone.`)) return;
    fetchApi('/api/QuoteRules/Delete', { ID: rule.ID }, 'post')
      .then((r: JsonResponse) => r.Success ? load() : alert(r.ErrorMessage));
  };

  // ---- cells ----
  const EnabledCell = (props: GridCustomCellProps) => {
    const item = props.dataItem as QuoteRuleViewModel;
    return (
      <td {...props.tdProps} style={{ textAlign: 'center' }}>
        <Switch size="small" checked={item.Enabled} disabled={item.IsDefault}
          onChange={(e) => toggleEnabled(item, e.value)} />
      </td>
    );
  };

  const NameCell = (props: GridCustomCellProps) => {
    const item = props.dataItem as QuoteRuleViewModel;
    return (
      <td {...props.tdProps}>
        <span style={{ fontWeight: 600 }}>{item.IsDefault ? 'Default' : item.Name}</span>
        {item.ModifiedByName && <div className="text-muted small">edited by {item.ModifiedByName}</div>}
      </td>
    );
  };

  const IfCell = (props: GridCustomCellProps) => (
    <td {...props.tdProps}>{conditionChips((props.dataItem as QuoteRuleViewModel).Conditions)}</td>
  );

  const ThenCell = (props: GridCustomCellProps) => (
    <td {...props.tdProps}>{actionBadges(props.dataItem as QuoteRuleViewModel)}</td>
  );

  const ActionsCell = (props: GridCustomCellProps) => {
    const item = props.dataItem as QuoteRuleViewModel;
    return (
      <td {...props.tdProps} style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        <Button fillMode="flat" size="small" svgIcon={pencilIcon} title="Edit" onClick={() => setEditing(item)} />
        <Button fillMode="flat" size="small" svgIcon={clockIcon} title="History" onClick={() => setHistoryRule(item)} />
        {/*!item.IsDefault &&
          <Button fillMode="flat" size="small" themeColor="error" svgIcon={trashIcon} title="Delete"
            onClick={() => remove(item)} />*/}
      </td>
    );
  };

  return (
    <>
      <Title string="Quote Rules" />
      <br />
      <Grid
        data={rules}
        dataItemKey="ID"
        sortable={false}
        rowReorderable={true}
        onRowReorder={onRowReorder}
        showLoader={loading}
      >
        <GridToolbar>
          <Button size="small" themeColor="primary" svgIcon={plusIcon} onClick={() => setEditing('new')}>
            New Rule
          </Button>
          <Button size="small" svgIcon={clockIcon} className="mr-2" onClick={() => setHistoryRule('all')}>
            Changelog
          </Button>
        </GridToolbar>
        <Column title="Priority" columnType="reorder" width={60} media="(min-width: 768px)" />
        <Column title="Enable" width={65} cells={{ data: EnabledCell }} />
        <Column title="Name" width={200} cells={{ data: NameCell }} />
        <Column title="If" media="(min-width: 768px)" cells={{ data: IfCell }} />
        <Column title="Then" media="(min-width: 768px)" cells={{ data: ThenCell }} />
        <Column title="Actions" width={78} cells={{ data: ActionsCell }} />
      </Grid>

      {editing && (
        <RuleEditor
          rule={editing === 'new' ? null : editing}
          sources={sources}
          vehicleTypes={vehicleTypes}
          frontInboxes={frontInboxes}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {historyRule && (
        <RuleHistory
          ruleId={historyRule === 'all' ? null : historyRule.ID}
          title={historyRule === 'all' ? 'Rule changes' : `${historyRule.Name || 'Default'} Changes`}
          onClose={() => setHistoryRule(null)}
        />
      )}
    </>
  );
};

export default QuoteAutomationRules;
